/**
 * ポータル: 経費（立替精算・購買申請）
 * タブ: 自分の申請／承認・経理（管理者）／科目マスタ（管理者）
 * 承認フロー: 上長承認 →（しきい値以上は役員承認）→ 経理確認 → 精算済み。訂正削除履歴つき（電帳法対応）。
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Receipt, Plus, Download, Paperclip, Settings2 } from 'lucide-react';
import * as api from '../../services/api';
import { Badge, Modal, StatusBadge, Tabs, fieldStyle, labelStyle, fmtDateTime, fileUrl, EmptyState } from './ui';

interface Category { id: string; name: string; display_order: number; is_active: number }
interface Expense {
  id: string; expense_type: string; applicant_user_id: string; applicant_name: string | null;
  expense_date: string; amount: number; payee: string | null; category_id: string | null; category_name?: string | null;
  description: string | null; receipt_path: string | null; receipt_name: string | null;
  status: string; status_label: string; reject_reason: string | null; created_at: string;
  events?: Array<{ action: string; actor_name: string | null; detail: string | null; created_at: string }>;
}

const EVENT_LABEL: Record<string, string> = {
  create: '申請', update: '修正', receipt_replace: '領収書差し替え',
  approve: '承認', reject: '却下', settle: '精算', withdraw: '取下げ',
};

const yen = (n: number) => `¥${n.toLocaleString()}`;

export default function PortalExpensesPage() {
  const user = useMemo(() => { try { return JSON.parse(localStorage.getItem('admin_user') ?? '{}'); } catch { return {}; } }, []);
  const isAdmin = ['system_admin', 'shore_admin'].includes(user?.user_role ?? '');

  const [tab, setTab] = useState('mine');
  const [mine, setMine] = useState<Expense[]>([]);
  const [all, setAll] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [detail, setDetail] = useState<Expense | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<Expense | null>(null);
  const [fStatus, setFStatus] = useState('');
  const [fMonth, setFMonth] = useState('');
  const [fPayee, setFPayee] = useState('');
  const [threshold, setThreshold] = useState<number | null>(null);

  const loadMine = useCallback(() => {
    api.get<{ data: Expense[] }>('/portal/expenses', { mine: '1' }).then(r => setMine(r.data ?? [])).catch(() => { /* noop */ });
  }, []);
  const loadAll = useCallback(() => {
    if (!isAdmin) return;
    const p: Record<string, string> = {};
    if (fStatus) p['status'] = fStatus;
    if (fMonth) p['month'] = fMonth;
    if (fPayee) p['payee'] = fPayee;
    api.get<{ data: Expense[] }>('/portal/expenses', p).then(r => setAll(r.data ?? [])).catch(() => { /* noop */ });
  }, [isAdmin, fStatus, fMonth, fPayee]);
  const loadCats = useCallback(() => {
    api.get<{ data: Category[] }>('/portal/expenses/categories', isAdmin ? { all: '1' } : {}).then(r => setCategories(r.data ?? [])).catch(() => { /* noop */ });
  }, [isAdmin]);

  useEffect(() => { loadMine(); loadCats(); }, [loadMine, loadCats]);
  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    if (isAdmin) api.get<{ data: { officer_threshold: number } }>('/portal/expenses/settings').then(r => setThreshold(r.data.officer_threshold)).catch(() => { /* noop */ });
  }, [isAdmin]);

  const openDetail = async (id: string) => {
    try { const r = await api.get<{ data: Expense }>(`/portal/expenses/${id}`); setDetail(r.data); }
    catch (e: unknown) { alert(e instanceof Error ? e.message : '取得に失敗しました'); }
  };

  const doAction = async (e: Expense, action: string, comment?: string) => {
    try {
      await api.post(`/portal/expenses/${e.id}/action`, { action, comment: comment ?? null });
      setDetail(null); loadMine(); loadAll();
    } catch (err: unknown) { alert(err instanceof Error ? err.message : '操作に失敗しました'); }
  };

  const list = (rows: Expense[], showApplicant: boolean) => (
    <div className="card" style={{ padding: 0, overflow: 'auto' }}>
      {rows.length === 0 ? <EmptyState img="/portal/empty-expenses.png" text="経費はまだありません" /> : (
        <table>
          <thead><tr>
            <th style={{ width: 95 }}>取引日</th>
            {showApplicant && <th style={{ width: 110 }}>申請者</th>}
            <th style={{ width: 90 }}>区分</th><th style={{ width: 110 }}>科目</th>
            <th style={{ width: 110, textAlign: 'right' }}>金額</th><th>取引先・内容</th>
            <th style={{ width: 55 }}>領収書</th><th style={{ width: 120 }}>状態</th>
          </tr></thead>
          <tbody>
            {rows.map(e => (
              <tr key={e.id} style={{ cursor: 'pointer' }} onClick={() => void openDetail(e.id)}>
                <td style={{ fontSize: 12.5 }}>{e.expense_date.replace(/-/g, '/')}</td>
                {showApplicant && <td style={{ fontSize: 12.5 }}>{e.applicant_name}</td>}
                <td>{e.expense_type === 'purchase' ? <Badge color="#7c3aed" bg="#ede9fe">購買</Badge> : <Badge color="#0f766e" bg="#e6f4f1">立替</Badge>}</td>
                <td style={{ fontSize: 12.5 }}>{e.category_name ?? ''}</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{yen(e.amount)}</td>
                <td style={{ fontSize: 12.5 }}>
                  {e.payee && <span style={{ fontWeight: 600 }}>{e.payee}</span>}
                  {e.description && <span style={{ color: '#6b7280' }}>　{e.description}</span>}
                </td>
                <td style={{ textAlign: 'center' }}>{e.receipt_path && <Paperclip size={13} color="#6b7280" />}</td>
                <td><StatusBadge status={e.status} label={e.status_label} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <div className="page-wrap">
      <div className="top-bar">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Receipt size={20} /> 経費
        </h1>
        <button className="btn-p" style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => setShowCreate(true)}>
          <Plus size={15} /> 経費を申請
        </button>
      </div>

      <Tabs active={tab} onChange={setTab} tabs={[
        { key: 'mine', label: '自分の申請' },
        ...(isAdmin ? [{ key: 'admin', label: '承認・経理' }, { key: 'cats', label: '科目マスタ' }] : []),
      ]} />

      {tab === 'mine' && list(mine, false)}

      {tab === 'admin' && isAdmin && (
        <>
          <div className="card" style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <select style={{ ...fieldStyle, width: 150 }} value={fStatus} onChange={e => setFStatus(e.target.value)}>
              <option value="">状態: すべて</option>
              <option value="pending">上長承認待ち</option>
              <option value="officer">役員承認待ち</option>
              <option value="checking">経理確認待ち</option>
              <option value="settled">精算済み</option>
              <option value="rejected">却下</option>
            </select>
            <input type="month" style={{ ...fieldStyle, width: 150 }} value={fMonth} onChange={e => setFMonth(e.target.value)} />
            <input style={{ ...fieldStyle, width: 180 }} placeholder="取引先で検索" value={fPayee}
              onChange={e => setFPayee(e.target.value)} />
            <a className="btn" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5, textDecoration: 'none' }}
              href={fileUrl(`/portal/expenses/export.csv${fMonth ? `?month=${fMonth}` : ''}`)}>
              <Download size={14} /> CSV出力
            </a>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#6b7280' }}>
              <Settings2 size={13} /> 役員承認のしきい値:
              <input type="number" style={{ ...fieldStyle, width: 110, padding: '5px 8px' }} value={threshold ?? ''}
                onChange={e => setThreshold(parseInt(e.target.value, 10) || 0)}
                onBlur={async () => { if (threshold !== null) { try { await api.put('/portal/expenses/settings', { officer_threshold: threshold }); } catch { /* noop */ } } }} />
              円以上
            </span>
          </div>
          {list(all, true)}
        </>
      )}

      {tab === 'cats' && isAdmin && (
        <CategoriesTab categories={categories} onChanged={loadCats} />
      )}

      {detail && (
        <ExpenseDetail e={detail} isAdmin={isAdmin} meId={user?.id ?? ''} categories={categories}
          onClose={() => setDetail(null)} onAction={doAction}
          onEdit={() => { setEditTarget(detail); setDetail(null); }}
          onReload={() => void openDetail(detail.id)} />
      )}

      {(showCreate || editTarget) && (
        <ExpenseEditor expense={editTarget} categories={categories.filter(c => c.is_active === 1)}
          onClose={() => { setShowCreate(false); setEditTarget(null); }}
          onSaved={() => { setShowCreate(false); setEditTarget(null); loadMine(); loadAll(); }} />
      )}
    </div>
  );
}

function ExpenseDetail({ e, isAdmin, meId, onClose, onAction, onEdit, onReload }: {
  e: Expense; isAdmin: boolean; meId: string; categories: Category[];
  onClose: () => void; onAction: (e: Expense, action: string, comment?: string) => void;
  onEdit: () => void; onReload: () => void;
}) {
  const [comment, setComment] = useState('');
  const mineAndPending = e.applicant_user_id === meId && e.status === 'pending';
  const row = (l: string, v: React.ReactNode) => (
    <div style={{ display: 'flex', fontSize: 13.5, padding: '5px 0', borderBottom: '1px solid #f8fafc' }}>
      <span style={{ width: 110, color: '#6b7280', flexShrink: 0 }}>{l}</span>
      <span style={{ color: '#111827' }}>{v}</span>
    </div>
  );
  return (
    <Modal width={640} onClose={onClose} title={
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {e.expense_type === 'purchase' ? '購買申請' : '立替経費'} {yen(e.amount)}
        <StatusBadge status={e.status} label={e.status_label} />
      </span>
    }>
      {row('申請者', e.applicant_name)}
      {row('取引日', e.expense_date.replace(/-/g, '/'))}
      {row('金額', <strong>{yen(e.amount)}</strong>)}
      {row('取引先', e.payee ?? '—')}
      {row('科目', e.category_name ?? '—')}
      {row('内容', e.description ?? '—')}
      {row('領収書', e.receipt_path
        ? <a href={fileUrl(`/portal/expenses/${e.id}/receipt`)} target="_blank" rel="noreferrer" style={{ color: '#0f766e', display: 'inline-flex', alignItems: 'center', gap: 5 }}><Paperclip size={13} />{e.receipt_name ?? '表示'}</a>
        : '—')}
      {e.reject_reason && row('却下理由', <span style={{ color: '#dc2626' }}>{e.reject_reason}</span>)}

      {mineAndPending && (
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button className="btn" onClick={onEdit}>修正する</button>
          <ReceiptReplace expenseId={e.id} onDone={onReload} />
          <button className="btn" style={{ color: '#dc2626' }} onClick={() => { if (confirm('この申請を取り下げますか？')) onAction(e, 'withdraw'); }}>取下げ</button>
        </div>
      )}

      {isAdmin && ['pending', 'officer', 'checking'].includes(e.status) && (
        <div style={{ marginTop: 16, background: '#f8fafc', borderRadius: 10, padding: 14 }}>
          <div style={{ ...labelStyle }}>承認操作（コメントは任意・却下時は理由として残ります）</div>
          <input style={{ ...fieldStyle, marginBottom: 10 }} value={comment} onChange={ev => setComment(ev.target.value)} placeholder="コメント" />
          <div style={{ display: 'flex', gap: 8 }}>
            {(e.status === 'pending' || e.status === 'officer') && (
              <button className="btn-p" onClick={() => onAction(e, 'approve', comment)}>
                {e.status === 'officer' ? '役員承認する' : '承認する'}
              </button>
            )}
            {e.status === 'checking' && (
              <button className="btn-p" style={{ background: '#15803d' }} onClick={() => onAction(e, 'settle', comment)}>精算済みにする</button>
            )}
            <button className="btn" style={{ color: '#dc2626' }} onClick={() => {
              if (!comment.trim()) { alert('却下の理由をコメントに入力してください'); return; }
              onAction(e, 'reject', comment);
            }}>却下する</button>
          </div>
        </div>
      )}

      {(e.events?.length ?? 0) > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={labelStyle}>履歴（訂正・削除の記録）</div>
          {e.events!.map((ev, i) => (
            <div key={i} style={{ fontSize: 12.5, color: '#4b5563', padding: '4px 0', borderBottom: '1px solid #f8fafc' }}>
              <span style={{ color: '#6b7280' }}>{fmtDateTime(ev.created_at)}</span>
              　<strong>{EVENT_LABEL[ev.action] ?? ev.action}</strong>　{ev.actor_name}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

function ReceiptReplace({ expenseId, onDone }: { expenseId: string; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <label className="btn" style={{ cursor: 'pointer' }}>
      {busy ? '差し替え中...' : '領収書の差し替え'}
      <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" style={{ display: 'none' }} disabled={busy} onChange={async e => {
        const f = e.target.files?.[0];
        if (!f) return;
        setBusy(true);
        try {
          const form = new FormData();
          form.append('receipt', f);
          await api.postForm(`/portal/expenses/${expenseId}/receipt`, form);
          onDone();
        } catch (err: unknown) { alert(err instanceof Error ? err.message : '失敗しました'); }
        finally { setBusy(false); e.target.value = ''; }
      }} />
    </label>
  );
}

function ExpenseEditor({ expense, categories, onClose, onSaved }: {
  expense: Expense | null; categories: Category[]; onClose: () => void; onSaved: () => void;
}) {
  const [type, setType] = useState(expense?.expense_type ?? 'expense');
  const [date, setDate] = useState(expense?.expense_date ?? new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState(expense ? String(expense.amount) : '');
  const [payee, setPayee] = useState(expense?.payee ?? '');
  const [categoryId, setCategoryId] = useState(expense?.category_id ?? '');
  const [description, setDescription] = useState(expense?.description ?? '');
  const [receipt, setReceipt] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const amt = parseInt(amount, 10);
    if (!date || !Number.isFinite(amt) || amt <= 0) { alert('取引日と金額を入力してください'); return; }
    setBusy(true);
    try {
      if (expense) {
        await api.put(`/portal/expenses/${expense.id}`, {
          expense_date: date, amount: amt, payee: payee || null, category_id: categoryId || null, description: description || null,
        });
      } else {
        const form = new FormData();
        form.append('expense_type', type);
        form.append('expense_date', date);
        form.append('amount', String(amt));
        if (payee) form.append('payee', payee);
        if (categoryId) form.append('category_id', categoryId);
        if (description) form.append('description', description);
        if (receipt) form.append('receipt', receipt);
        await api.postForm('/portal/expenses', form);
      }
      onSaved();
    } catch (e: unknown) { alert(e instanceof Error ? e.message : '保存に失敗しました'); }
    finally { setBusy(false); }
  };

  return (
    <Modal width={560} title={expense ? '経費の修正' : '経費を申請'} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {!expense && (
          <div>
            <label style={labelStyle}>区分</label>
            <select style={fieldStyle} value={type} onChange={e => setType(e.target.value)}>
              <option value="expense">立替経費の精算（支払い済みのもの）</option>
              <option value="purchase">購買申請（買う前の稟議）</option>
            </select>
          </div>
        )}
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>取引日 *</label>
            <input type="date" style={fieldStyle} value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>金額（円） *</label>
            <input type="number" style={fieldStyle} value={amount} onChange={e => setAmount(e.target.value)} placeholder="12800" />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>取引先（店名・会社名）</label>
            <input style={fieldStyle} value={payee} onChange={e => setPayee(e.target.value)} placeholder="例: JR西日本" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>科目</label>
            <select style={fieldStyle} value={categoryId} onChange={e => setCategoryId(e.target.value)}>
              <option value="">選択してください</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label style={labelStyle}>内容</label>
          <input style={fieldStyle} value={description} onChange={e => setDescription(e.target.value)} placeholder="例: 神戸港への出張（往復）" />
        </div>
        {!expense && (
          <div>
            <label style={labelStyle}>領収書（写真・PDF）</label>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={e => setReceipt(e.target.files?.[0] ?? null)} />
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn" onClick={onClose}>キャンセル</button>
          <button className="btn-p" disabled={busy} onClick={() => void save()}>{busy ? '送信中...' : expense ? '修正する' : '申請する'}</button>
        </div>
      </div>
    </Modal>
  );
}

function CategoriesTab({ categories, onChanged }: { categories: Category[]; onChanged: () => void }) {
  const [name, setName] = useState('');
  return (
    <div className="card" style={{ padding: 16, maxWidth: 560 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <input style={{ ...fieldStyle, flex: 1 }} value={name} onChange={e => setName(e.target.value)} placeholder="新しい科目名" />
        <button className="btn-p" onClick={async () => {
          if (!name.trim()) return;
          try { await api.post('/portal/expenses/categories', { name: name.trim() }); setName(''); onChanged(); }
          catch (e: unknown) { alert(e instanceof Error ? e.message : '追加に失敗しました'); }
        }}>追加</button>
      </div>
      {categories.map(c => (
        <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '1px solid #f8fafc' }}>
          <span style={{ fontSize: 13.5, flex: 1, color: c.is_active ? '#111827' : '#6b7280' }}>
            {c.name}{c.is_active === 0 && '（無効）'}
          </span>
          <button className="btn" style={{ fontSize: 12 }} onClick={async () => {
            try { await api.put(`/portal/expenses/categories/${c.id}`, { is_active: c.is_active === 0 }); onChanged(); }
            catch (e: unknown) { alert(e instanceof Error ? e.message : '失敗しました'); }
          }}>{c.is_active === 1 ? '無効にする' : '有効にする'}</button>
          <button className="btn" style={{ fontSize: 12, color: '#dc2626' }} onClick={async () => {
            if (!confirm(`科目「${c.name}」を削除しますか？`)) return;
            try { await api.del(`/portal/expenses/categories/${c.id}`); onChanged(); }
            catch (e: unknown) { alert(e instanceof Error ? e.message : '失敗しました'); }
          }}>削除</button>
        </div>
      ))}
    </div>
  );
}
